import {
    DropDown,
    getMaskFromName,
    Input,
    useWorkplaceState,
    ValidatedDropDownList,
    ValidatedInput,
} from '@finbeat/react-toolkit';
import { AxiosError } from 'axios';
import { getChannels } from 'axios/merchantApi';
import { getOutletsAll } from 'axios/outletsApi';
import { BOValidatedDatePicker } from 'base-components/BOValidatedDatePicker/BOValidatedDatePicker';
import useApi from 'hooks/useApi';
import useAuth from 'hooks/useAuth';
import { AxiosErrorResponse } from 'models/api/axiosErrorResponse';
import { OrderFilter } from 'models/api/order';
import { useOrderLists } from 'providers/OrderListsProvider';
import { ChangeEvent, useState } from 'react';
import { Controller, Path, useFormContext } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import {
    Accordion,
    AccordionBody,
    AccordionHeader,
    AccordionItem,
    Col,
    FormGroup,
    Row,
    Spinner,
} from 'reactstrap';
import { docMask } from 'utils/masks/docMasks';
import { MerchantFilterType } from 'models/api/merchant';

type CheckboxesFilterBlockType = {
    title: string;
    queryLabel: string;
    items: DropDown[];
    name: Path<OrderFilter>;
    loading: boolean;
};

const CheckboxesFilterBlock = ({
    title,
    queryLabel,
    items,
    loading,
    name,
}: CheckboxesFilterBlockType) => {
    const [open, setOpen] = useState('');
    const { control } = useFormContext<OrderFilter>();

    const toggle = () => {
        setOpen(open === '' ? queryLabel : '');
    };

    const addInArray = (value: string[], e: ChangeEvent<HTMLInputElement>) => {
        const index = value.indexOf(e.target.value);

        if (index > -1) {
            value.splice(index, 1);
        } else {
            value.push(e.target.value);
        }

        return value;
    };

    const accProps = { toggle: toggle };

    return loading ? (
        <Spinner />
    ) : (
        <Accordion flush open={open} {...accProps}>
            <AccordionItem>
                <AccordionHeader
                    style={{
                        color: 'black !important',
                        marginRight: '5px',
                    }}
                    onClick={toggle}
                    targetId={queryLabel}
                >
                    <Trans i18nKey={title} />
                </AccordionHeader>
                <AccordionBody accordionId={queryLabel}>
                    <FormGroup floating>
                        <Controller<OrderFilter>
                            name={name}
                            control={control}
                            render={({ field: { onChange, value } }) => {
                                const valueArray = (value ?? []) as string[];
                                return (
                                    <Row>
                                        {items.map(({ id, name }, idx) => (
                                            <div className="col-6 form-check" key={idx}>
                                                <Input<any>
                                                    id={`${id}`}
                                                    type="checkbox"
                                                    value={`${id}`}
                                                    name={`${id}`}
                                                    checked={valueArray.includes(
                                                        (id ?? '').toString()
                                                    )}
                                                    onChange={(e) => {
                                                        onChange(addInArray(valueArray, e));
                                                    }}
                                                    label={name ?? undefined}
                                                />
                                            </div>
                                        ))}
                                    </Row>
                                );
                            }}
                        />
                    </FormGroup>
                </AccordionBody>
            </AccordionItem>
        </Accordion>
    );
};

const ignoredPeriodProps: Partial<keyof OrderFilter>[] & string[] = [
    'loanId',
    'orderId',
    'phone',
    'docNum',
    'fio',
];

const OrderFilterModal = () => {
    const { t } = useTranslation();
    const getIsIgnoredPeriod = (updatedValue: OrderFilter) => {
        return !!Object.entries(updatedValue).find(
            ([key, value]) => ignoredPeriodProps.includes(key) && value
        );
    };

    const {
        orderStatuses,
        queryOrderStatuses,
        loanStatuses,
        queryDicts,
        operators,
        tradeOrgs,
        brandCodes,
        productTypes,
        queryBrandCodes,
        sources,
        queryOperators,
        queryTradeOrgs,
    } = useOrderLists();

    const api = useApi();
    const { hasRole } = useAuth();
    const { handleError } = useWorkplaceState();
    const { watch, setValue } = useFormContext<OrderFilter>();
    watch((value, { name, type }) => {
        if (type === 'change' && name) {
            setValue('isIgnorePeriod', getIsIgnoredPeriod({ [name]: value }));
        }
    });

    const tradeOrgId = watch('tradeOrgId');

    const { isFetching: outletsFetching, data: outlets } = useQuery(
        ['Outlets', tradeOrgId],
        () => getOutletsAll(api, tradeOrgId ?? null),
        {
            enabled: !!tradeOrgId,
            onError: (e: AxiosError<AxiosErrorResponse>) => handleError(e.response?.data),
        }
    );

    const { isFetching: isChannelsFetching, data: channels } = useQuery(
        'orderChannels',
        () => getChannels(api, false),
        {
            onError: (err) => handleError(err),
            enabled: hasRole('merchantGetChannels'),
        }
    );

    return (
        <>
            <Row>
                <Col>
                    <ValidatedInput
                        id="loan-id"
                        name="loanId"
                        type="number"
                        label={t('BackOfficeLoanId')}
                        maskProps={{
                            //TODO временное ограничение, поправить с переходом на новые маски
                            mask: '9999999999',
                            maskPlaceholder: null,
                        }}
                    />
                </Col>
                <Col>
                    <ValidatedInput
                        id="order-id"
                        name="orderId"
                        type="number"
                        label={t('BackOfficeReportHeaderOrderId')}
                        maskProps={{
                            //TODO временное ограничение, поправить с переходом на новые маски
                            mask: '9999999999',
                            maskPlaceholder: null,
                        }}
                    />
                </Col>
            </Row>
            <Row>
                <Col>
                    <BOValidatedDatePicker
                        id="ds-start-date"
                        name="sd"
                        inputProps={{ label: t('BackOfficeStartDate') }}
                    />
                </Col>
                <Col>
                    <BOValidatedDatePicker
                        id="ds-end-date"
                        name="ed"
                        inputProps={{ label: t('BackOfficeEndDate') }}
                    />
                </Col>
            </Row>
            <Row>
                <Col>
                    <ValidatedInput
                        id="phone"
                        name="phone"
                        label={t('BackOfficeMobilePhone')}
                        maskProps={{
                            mask: getMaskFromName('Uzbekistan'),
                        }}
                    />
                </Col>
                <Col>
                    <ValidatedInput
                        id="doc-num"
                        name="docNum"
                        label={t('BackOfficeReportHeaderDocNum')}
                        maskProps={{
                            mask: docMask,
                            preserveMask: true,
                        }}
                    />
                </Col>
            </Row>
            <Row>
                <ValidatedDropDownList
                    name="tradeOrgId"
                    label={t('BackOfficeTradeOrg')}
                    isLoading={queryTradeOrgs.isLoading}
                    items={tradeOrgs ?? []}
                    onValueChange={() => setValue('outletId', null)}
                />
                <ValidatedDropDownList
                    name="outletId"
                    label={t('BackOfficeOutlets')}
                    disabled={tradeOrgId == null || !outlets}
                    isLoading={outletsFetching}
                    items={outlets ?? []}
                />
            </Row>
            <Row>
                <Col xs="6">
                    <ValidatedDropDownList
                        name="orderSource"
                        items={sources}
                        isLoading={queryDicts.isFetching}
                        disabled={!sources.length}
                        label={t('BackOfficeSource')}
                    />
                </Col>
                {hasRole('merchantGetChannels') && (
                    <Col xs="6">
                        <ValidatedDropDownList
                            name="channel"
                            items={channels ?? []}
                            isLoading={isChannelsFetching}
                            disabled={!channels?.length}
                            label={t('BackOfficeChannel')}
                        />
                    </Col>
                )}
            </Row>
            {hasRole('visibleBrandCodesFilter') && (
                <Row>
                    <Col>
                        <ValidatedDropDownList<MerchantFilterType>
                            name="brandCode"
                            items={brandCodes ?? []}
                            isLoading={queryBrandCodes.isFetching}
                            disabled={!brandCodes?.length}
                            label={t('BackOfficeBrandCode')}
                        />
                    </Col>
                </Row>
            )}
            <Row>
                <Col xs="6">
                    <ValidatedDropDownList
                        name="productTypeId"
                        items={productTypes}
                        disabled={!productTypes.length}
                        label={t('BackOfficeProductType')}
                    />
                </Col>
            </Row>
            <Row>
                <Col>
                    <CheckboxesFilterBlock
                        name="orderStatuses"
                        title="BackOfficeOrderStatuses"
                        queryLabel="dictOrderStatusesList"
                        items={orderStatuses}
                        loading={queryOrderStatuses.isFetching}
                    />
                </Col>
            </Row>
            <Row>
                <Col>
                    <CheckboxesFilterBlock
                        name="loanStatuses"
                        title="BackOfficeLoanStatuses"
                        queryLabel="dictLoanStatusesList"
                        items={loanStatuses}
                        loading={queryDicts.isFetching}
                    />
                </Col>
            </Row>
            <FormGroup>
                <ValidatedInput
                    id="name"
                    name="fio"
                    label={t('BackOfficeReportHeaderBeneficiaryName')}
                />
            </FormGroup>
            <Row>
                <Col>
                    <CheckboxesFilterBlock
                        name="operators"
                        title="BackOfficeOperators"
                        queryLabel="operatorsList"
                        items={operators.map<DropDown>((x) => ({
                            id: x.id,
                            name: `${x.lastName} ${x.firstName}`,
                        }))}
                        loading={queryOperators.isFetching}
                    />
                </Col>
            </Row>
        </>
    );
};

export default OrderFilterModal;
